// Section · The Feature Matrix.
//
// Source of truth: STRIKZA_OddsJam_Gaps_v1.0.docx — fifteen named
// build specifications mapped to BIG6 pillar, tier gate, and build
// phase. The matrix is grouped by tier (the lens a visitor cares
// about) and shows phase + pillar at a glance.
//
// Doctrinal posture:
//   - Every entry is a real spec. The dataset
//     `oddsjam-gaps-data.ts` is the single source of truth. When a
//     gap ships, status flips there, not in this component.
//   - Phase badges (Pre-Launch / Phase 1 / Phase 2) are honest about
//     what's shipping when. No "Live" claim until the actual feature
//     is wired end-to-end.
//   - Free Daily Signal (GAP-14) is included on All Tiers including
//     the future Free tier. It is doctrinal acquisition — not a
//     teaser.

import { ODDSJAM_GAPS, type OddsJamGap, type TierGate, type BuildPhase } from "./landing-data.ts";

const TIER_GROUPS: ReadonlyArray<{
  tier: TierGate;
  label: string;
  sub: string;
}> = [
  {
    tier: "All Tiers",
    label: "All tiers — including Free",
    sub: "Every paying tier and the doctrinal Free tier ship with these.",
  },
  {
    tier: "Sika Pro+",
    label: "Sika Pro and above",
    sub: "Goal-driven layer + transparency. Where Sika starts managing your chain.",
  },
  {
    tier: "Pro+",
    label: "Pro and above",
    sub: "Multi-chain operators. Historical data + line-movement push alerts.",
  },
  {
    tier: "Elite+",
    label: "Elite and above",
    sub: "The arbitrage / promo / middling layer. For users with capital across multiple books.",
  },
];

const PHASE_COLOR: { readonly [k in BuildPhase]: string } = {
  "Pre-Launch": "var(--accent)",
  "Phase 1": "var(--success)",
  "Phase 2": "var(--text-muted)",
};

function GapCard({ gap }: { gap: OddsJamGap }): JSX.Element {
  return (
    <div
      style={{
        position: "relative",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        padding: "22px 22px 24px",
        minHeight: 180,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 14,
          gap: 12,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 10,
            letterSpacing: "0.18em",
            color: "var(--accent)",
            textTransform: "uppercase",
          }}
        >
          {gap.id}
        </span>
        <span
          className="mono"
          style={{
            fontSize: 9,
            letterSpacing: "0.16em",
            color: PHASE_COLOR[gap.phase],
            textTransform: "uppercase",
            border: `1px solid ${PHASE_COLOR[gap.phase]}`,
            padding: "3px 8px",
            whiteSpace: "nowrap",
          }}
        >
          {gap.phase}
        </span>
      </div>
      <div
        className="serif"
        style={{
          fontSize: 18,
          color: "var(--text)",
          fontStyle: "italic",
          fontWeight: 500,
          lineHeight: 1.25,
          marginBottom: 10,
        }}
      >
        {gap.name}
      </div>
      <div
        style={{
          fontSize: 13,
          color: "var(--text-muted)",
          lineHeight: 1.55,
          marginBottom: 14,
          flex: 1,
        }}
      >
        {gap.tagline}
      </div>
      <div
        className="mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.14em",
          color: "var(--text-dim)",
          textTransform: "uppercase",
          paddingTop: 12,
          borderTop: "1px solid var(--border)",
        }}
      >
        BIG6 · {gap.pillars.join(" · ")}
      </div>
    </div>
  );
}

export function FeatureMatrix(): JSX.Element {
  return (
    <section
      id="features"
      className="sz-l-section"
      style={{
        position: "relative",
        padding: "140px 0 160px",
        borderTop: "1px solid var(--border)",
        background: "linear-gradient(180deg, #0C0B08 0%, #0A0A0A 100%)",
      }}
    >
      <div className="container" style={{ position: "relative" }}>
        <div
          className="sz-l-section-header"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.6fr",
            gap: 80,
            marginBottom: 72,
          }}
        >
          <div>
            <span className="bracket">Section · The matrix</span>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: "0.18em",
                color: "var(--accent)",
                textTransform: "uppercase",
                marginTop: 18,
              }}
            >
              15 named specs · 6 BIG6 pillars · 4 tier gates
            </div>
          </div>
          <div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                lineHeight: 1.02,
                color: "var(--text)",
                marginBottom: 22,
                letterSpacing: "-0.01em",
              }}
            >
              Fifteen specs.{" "}
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>No marketing fluff.</em>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--text-muted)",
                maxWidth: 640,
                lineHeight: 1.55,
              }}
            >
              The OddsJam playbook for sharp bettors — adapted to Stake, Betway, SportyBet and
              1xBet, run through the 18-agent engine, sealed in the Daily Intelligence Ledger, and
              gated to the tier each spec was scoped for. If a feature isn&apos;t in this matrix, it
              isn&apos;t a STRIKZA feature.
            </p>
          </div>
        </div>

        {TIER_GROUPS.map((group) => {
          const gaps = ODDSJAM_GAPS.filter((g) => g.tier === group.tier);
          if (gaps.length === 0) return null;
          return (
            <div
              key={group.tier}
              style={{
                marginTop: 56,
                paddingTop: 32,
                borderTop: "1px solid var(--border)",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "end",
                  gap: 24,
                  marginBottom: 28,
                }}
              >
                <div>
                  <div
                    className="display"
                    style={{
                      fontSize: 26,
                      color: "var(--text)",
                      letterSpacing: "0.005em",
                      marginBottom: 6,
                    }}
                  >
                    {group.label}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "var(--text-muted)",
                      lineHeight: 1.5,
                      maxWidth: 640,
                    }}
                  >
                    {group.sub}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.18em",
                      color: "var(--text-dim)",
                      textTransform: "uppercase",
                    }}
                  >
                    Specs in this gate
                  </div>
                  <div
                    className="display"
                    style={{
                      fontSize: 28,
                      color: "var(--accent)",
                      letterSpacing: "0.01em",
                      marginTop: 4,
                    }}
                  >
                    {gaps.length}
                  </div>
                </div>
              </div>

              <div
                className={`sz-l-feature-grid sz-l-feature-grid-${group.tier
                  .toLowerCase()
                  .replace(/[^a-z]+/g, "-")
                  .replace(/^-|-$/g, "")}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 16,
                }}
              >
                {gaps.map((g) => (
                  <GapCard key={g.id} gap={g} />
                ))}
              </div>
            </div>
          );
        })}

        {/* Phase legend */}
        <div
          style={{
            marginTop: 56,
            padding: "24px 28px",
            border: "1px solid var(--border)",
            background: "rgba(17, 17, 17, 0.6)",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
          }}
        >
          {(["Pre-Launch", "Phase 1", "Phase 2"] as readonly BuildPhase[]).map((phase) => (
            <div
              key={phase}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  color: PHASE_COLOR[phase],
                  textTransform: "uppercase",
                  border: `1px solid ${PHASE_COLOR[phase]}`,
                  padding: "3px 8px",
                  width: "fit-content",
                }}
              >
                {phase}
              </span>
              <span style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.45 }}>
                {phase === "Pre-Launch"
                  ? "Shipping before the World Cup 2026 pilot window."
                  : phase === "Phase 1"
                    ? "Within the first 6 months post-launch."
                    : "Year-2 expansion tier."}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
